import {
  type CreditNoteKind,
  type CreditNoteListQuery,
  type InvoiceListQuery,
  type PaymentMethod,
  paymentMethodSchema,
  type QuotationListQuery,
  type QuotationStatus,
} from '@sahab/contracts'
import { z } from 'zod'
import { isIsoDate } from '../../lib/dates'

/**
 * The sales lists live in the URL (shareable, back-button safe). Every key is optional and an
 * invalid value silently falls back to the default instead of breaking the page.
 */
export const PAGE_SIZES = [25, 50, 100] as const
type PageSize = (typeof PAGE_SIZES)[number]

const page = z.number().int().min(1).optional().catch(undefined)
const pageSize = z
  .number()
  .refine((n): n is PageSize => (PAGE_SIZES as readonly number[]).includes(n))
  .optional()
  .catch(undefined)
const isoDate = z
  .string()
  .refine((v) => isIsoDate(v))
  .optional()
  .catch(undefined)
// A hand-written `?q=354` arrives as a number (the router parses JSON): keep it as text.
const q = z.coerce.string().trim().max(200).optional().catch(undefined)
const uuid = z.uuid().optional().catch(undefined)

/** A path inside this app (no other origin, no protocol-relative `//host` or `/\host`). */
export function isAppPath(path: string): boolean {
  return path.startsWith('/') && !path.startsWith('//') && !path.startsWith('/\\') && path.length <= 2000
}

/**
 * Where to go back to after saving a document started from somewhere else (a list row, another
 * document): that list with its view, search and page, or that document. Anything but a path in
 * this app is dropped.
 */
const returnTo = z.string().refine(isAppPath).optional().catch(undefined)

function sortOf<const F extends string>(fields: readonly F[]) {
  const valid = (value: string): value is F | `-${F}` =>
    (fields as readonly string[]).includes(value.startsWith('-') ? value.slice(1) : value)
  return {
    valid,
    schema: z.string().refine(valid).optional().catch(undefined),
  }
}

/**
 * Lists open with the newest documents first. The sort cycle in a column header is ascending →
 * descending → default; for the date column the default *is* descending, so its next step is ascending.
 */
export function nextListSort<S extends string>(
  next: string | undefined,
  current: string,
  defaultSort: string,
  valid: (value: string) => value is S,
): S | undefined {
  const asc = defaultSort.replace(/^-/, '')
  if (next === undefined) return current === defaultSort && valid(asc) ? asc : undefined
  if (next === defaultSort) return undefined
  return valid(next) ? next : undefined
}

// ── invoices ─────────────────────────────────────────────────────────────────

export const INVOICE_SORT = sortOf([
  'invoiceDate',
  'dueDate',
  'number',
  'total',
  'balance',
  'customer',
] as const)
export const INVOICE_DEFAULT_SORT = '-invoiceDate'
/** The list's view tabs after All (the contract's INVOICE_VIEWS; pages/invoices.md §6.1). */
export const INVOICE_VIEWS = ['drafts', 'open', 'overdue', 'paid', 'void'] as const
export type InvoiceView = (typeof INVOICE_VIEWS)[number]

/** What is owed is chased oldest due first; the other views read newest first. */
export function invoiceDefaultSort(view: InvoiceView | undefined): string {
  return view === 'open' || view === 'overdue' ? 'dueDate' : INVOICE_DEFAULT_SORT
}

export const invoiceSearchSchema = z.object({
  page,
  pageSize,
  sort: INVOICE_SORT.schema,
  // A hand-written `?q=354` arrives as a number (the router parses JSON): keep it as text.
  q: z.coerce.string().trim().max(200).optional().catch(undefined),
  // `draft` was the Drafts view's key before the views were renamed; old links still open it.
  view: z
    .preprocess((v) => (v === 'draft' ? 'drafts' : v), z.enum(INVOICE_VIEWS))
    .optional()
    .catch(undefined),
  kind: z.enum(['INVOICE', 'DEBIT_NOTE']).optional().catch(undefined),
  customerId: uuid,
  /** Documents tagged to this project. */
  projectId: uuid,
  from: isoDate,
  to: isoDate,
  /** Drafts view: only the ones I created (the "Mine" chip). */
  createdBy: z.literal('me').optional().catch(undefined),
  /** The salesperson credited (ADR-049): `me`, or a user. */
  salespersonId: z
    .union([z.literal('me'), z.uuid()])
    .optional()
    .catch(undefined),
})
export type InvoiceSearch = z.infer<typeof invoiceSearchSchema>

export type InvoiceListParams = Omit<InvoiceListQuery, 'page' | 'pageSize' | 'open' | 'paymentStatus'> & {
  page: number
  pageSize: number
  sort: string
  /** Posted documents with an open balance. */
  open?: boolean | undefined
  /** Posted documents whose derived payment status is this one (e.g. OVERDUE). */
  paymentStatus?: 'UNPAID' | 'PARTIAL' | 'PAID' | 'OVERDUE' | undefined
}

/** The list's filters other than the view: what the view tabs count (`GET /sales/invoices/summary`). */
export type InvoiceSummaryParams = Pick<
  InvoiceListParams,
  'q' | 'kind' | 'customerId' | 'from' | 'to' | 'createdBy' | 'projectId' | 'salespersonId'
>

export function toInvoiceListQuery(search: InvoiceSearch): InvoiceListParams {
  const view = search.view
  return {
    page: search.page ?? 1,
    pageSize: search.pageSize ?? PAGE_SIZES[0],
    sort: search.sort ?? invoiceDefaultSort(view),
    q: search.q || undefined,
    kind: search.kind,
    status: view === 'drafts' ? 'DRAFT' : view === 'void' ? 'VOID' : undefined,
    open: view === 'open' ? true : undefined,
    paymentStatus: view === 'overdue' ? 'OVERDUE' : view === 'paid' ? 'PAID' : undefined,
    customerId: search.customerId,
    projectId: search.projectId,
    from: search.from,
    to: search.to,
    createdBy: search.createdBy,
    salespersonId: search.salespersonId,
  }
}

export function toInvoiceSummaryQuery(query: InvoiceListParams): InvoiceSummaryParams {
  return {
    q: query.q,
    kind: query.kind,
    customerId: query.customerId,
    projectId: query.projectId,
    from: query.from,
    to: query.to,
    createdBy: query.createdBy,
    salespersonId: query.salespersonId,
  }
}

/**
 * New invoice: `?kind=DEBIT_NOTE` starts a debit note, `?customerId=` preselects the customer,
 * `?from=<id>` duplicates that invoice or debit note (any status) into a new draft.
 */
export const newInvoiceSearchSchema = z.object({
  kind: z.enum(['INVOICE', 'DEBIT_NOTE']).optional().catch(undefined),
  customerId: uuid,
  from: uuid,
})

/** Edit a draft: `?focus=invoiceDate` starts in the invoice date ("Change date" from the post confirmation). */
export const editInvoiceSearchSchema = z.object({
  focus: z.enum(['invoiceDate']).optional().catch(undefined),
})

/** The print page: `?print=1` opens the browser's print dialog once the sheet is ready. */
export const printSearchSchema = z.object({
  print: z.coerce.boolean().optional().catch(undefined),
})

// ── quotations ───────────────────────────────────────────────────────────────

export const QUOTATION_SORT = sortOf(['quotationDate', 'validUntil', 'number', 'total', 'customer'] as const)
export const QUOTATION_DEFAULT_SORT = '-quotationDate'
export const QUOTATION_STATUSES = [
  'PENDING',
  'APPROVED',
  'REJECTED',
  'CONVERTED',
] as const satisfies readonly QuotationStatus[]
/**
 * The list's view tabs after All (the contract's QUOTATION_VIEWS, sales-documents §2.6): they
 * partition the list, so Pending and Approved leave out the expired offers.
 */
export const QUOTATION_VIEWS = ['pending', 'approved', 'expired', 'converted', 'rejected'] as const
export type QuotationView = (typeof QUOTATION_VIEWS)[number]

/** Open offers are chased by their validity, soonest first; an expired one by how long ago. */
export function quotationDefaultSort(view: QuotationView | undefined): string {
  return view === 'pending' || view === 'approved'
    ? 'validUntil'
    : view === 'expired'
      ? '-validUntil'
      : QUOTATION_DEFAULT_SORT
}

export const quotationSearchSchema = z.object({
  page,
  pageSize,
  sort: QUOTATION_SORT.schema,
  q,
  view: z
    .preprocess((v) => (typeof v === 'string' ? v.toLowerCase() : v), z.enum(QUOTATION_VIEWS))
    .optional()
    .catch(undefined),
  customerId: uuid,
  /** Quotations tagged to this project or that started it. */
  projectId: uuid,
  from: isoDate,
  to: isoDate,
  createdBy: z.literal('me').optional().catch(undefined),
})
export type QuotationSearch = z.infer<typeof quotationSearchSchema>

export type QuotationListParams = Omit<QuotationListQuery, 'page' | 'pageSize' | 'expired'> & {
  page: number
  pageSize: number
  sort: string
  expired?: boolean | undefined
}

export type QuotationSummaryParams = Pick<
  QuotationListParams,
  'q' | 'customerId' | 'from' | 'to' | 'createdBy' | 'projectId'
>

const QUOTATION_VIEW_STATUS = {
  pending: 'PENDING',
  approved: 'APPROVED',
  expired: undefined,
  converted: 'CONVERTED',
  rejected: 'REJECTED',
} as const satisfies Record<QuotationView, QuotationStatus | undefined>

export function toQuotationListQuery(search: QuotationSearch): QuotationListParams {
  const view = search.view
  return {
    page: search.page ?? 1,
    pageSize: search.pageSize ?? PAGE_SIZES[0],
    sort: search.sort ?? quotationDefaultSort(view),
    q: search.q || undefined,
    status: view ? QUOTATION_VIEW_STATUS[view] : undefined,
    expired: view === 'expired' ? true : view === 'pending' || view === 'approved' ? false : undefined,
    customerId: search.customerId,
    projectId: search.projectId,
    from: search.from,
    to: search.to,
    createdBy: search.createdBy,
  }
}

export function toQuotationSummaryQuery(query: QuotationListParams): QuotationSummaryParams {
  return {
    q: query.q,
    customerId: query.customerId,
    projectId: query.projectId,
    from: query.from,
    to: query.to,
    createdBy: query.createdBy,
  }
}

/** New quotation: `?customerId=` preselects the customer, `?from=<id>` copies that quotation (any status). */
export const newQuotationSearchSchema = z.object({ customerId: uuid, from: uuid })

// ── receipts ─────────────────────────────────────────────────────────────────

export const RECEIPT_SORT = sortOf(['receiptDate', 'number', 'amount', 'unallocated', 'customer'] as const)
export const RECEIPT_DEFAULT_SORT = '-receiptDate'
/** The list's view tabs after All (the contract's RECEIPT_VIEWS). */
export const RECEIPT_VIEWS = ['withCredit', 'void'] as const
export type ReceiptView = (typeof RECEIPT_VIEWS)[number]

/** Credit waiting to be applied is worked oldest first; the other views read newest first. */
export function receiptDefaultSort(view: ReceiptView | undefined): string {
  return view === 'withCredit' ? 'receiptDate' : RECEIPT_DEFAULT_SORT
}

export const receiptSearchSchema = z.object({
  page,
  pageSize,
  sort: RECEIPT_SORT.schema,
  q,
  view: z.enum(RECEIPT_VIEWS).optional().catch(undefined),
  method: paymentMethodSchema.optional().catch(undefined),
  /** The cash or bank account received into. */
  account: uuid,
  customerId: uuid,
  from: isoDate,
  to: isoDate,
})
export type ReceiptSearch = z.infer<typeof receiptSearchSchema>

export interface ReceiptListParams {
  page: number
  pageSize: number
  sort: string
  q?: string | undefined
  status?: 'POSTED' | 'VOID' | undefined
  /** Posted receipts with credit left to allocate or refund. */
  hasOpenCredit?: boolean | undefined
  method?: PaymentMethod | undefined
  treasuryAccountId?: string | undefined
  customerId?: string | undefined
  from?: string | undefined
  to?: string | undefined
  createdBy?: string | undefined
}

export type ReceiptSummaryParams = Omit<
  ReceiptListParams,
  'page' | 'pageSize' | 'sort' | 'status' | 'hasOpenCredit'
>

export function toReceiptListQuery(search: ReceiptSearch): ReceiptListParams {
  const view = search.view
  return {
    page: search.page ?? 1,
    pageSize: search.pageSize ?? PAGE_SIZES[0],
    sort: search.sort ?? receiptDefaultSort(view),
    q: search.q || undefined,
    status: view === 'void' ? 'VOID' : undefined,
    hasOpenCredit: view === 'withCredit' ? true : undefined,
    method: search.method,
    treasuryAccountId: search.account,
    customerId: search.customerId,
    from: search.from,
    to: search.to,
  }
}

export function toReceiptSummaryQuery(query: ReceiptListParams): ReceiptSummaryParams {
  return {
    q: query.q,
    method: query.method,
    treasuryAccountId: query.treasuryAccountId,
    customerId: query.customerId,
    from: query.from,
    to: query.to,
    createdBy: query.createdBy,
  }
}

/**
 * Record a receipt: `?customerId=` preselects the customer, `?invoiceId=` settles that invoice,
 * `?returnTo=` is where saving goes back to (the invoice or the list row it was started from).
 */
/** `replacesChequeId`: a replacement for that bounced cheque (ADR-046), for its amount. */
export const newReceiptSearchSchema = z.object({
  customerId: uuid,
  invoiceId: uuid,
  returnTo,
  replacesChequeId: uuid,
})

/**
 * A voucher's print page (receipt, refund): `?paper=a4` prints on A4 portrait instead of the
 * voucher book's A5 landscape; `?print=1` opens the print dialog once the sheet is ready.
 */
export const voucherPrintSearchSchema = z.object({
  paper: z.enum(['a5', 'a4']).optional().catch(undefined),
  print: z.coerce.boolean().optional().catch(undefined),
})

// ── credit notes ─────────────────────────────────────────────────────────────

export const CREDIT_NOTE_SORT = sortOf([
  'noteDate',
  'number',
  'total',
  'createdAt',
  'unallocated',
  'customer',
] as const)
export const CREDIT_NOTE_DEFAULT_SORT = '-noteDate'
export const CREDIT_NOTE_KINDS = ['RETURN', 'ALLOWANCE'] as const satisfies readonly CreditNoteKind[]
/** The list's view tabs after All (the contract's CREDIT_NOTE_VIEWS). */
export const CREDIT_NOTE_VIEWS = ['drafts', 'withCredit', 'void'] as const
export type CreditNoteView = (typeof CREDIT_NOTE_VIEWS)[number]

/** Drafts have no number yet: the newest made first. Credit is applied oldest first. */
export function creditNoteDefaultSort(view: CreditNoteView | undefined): string {
  return view === 'drafts' ? '-createdAt' : view === 'withCredit' ? 'noteDate' : CREDIT_NOTE_DEFAULT_SORT
}

export const creditNoteSearchSchema = z.object({
  page,
  pageSize,
  sort: CREDIT_NOTE_SORT.schema,
  q,
  view: z.enum(CREDIT_NOTE_VIEWS).optional().catch(undefined),
  kind: z.enum(CREDIT_NOTE_KINDS).optional().catch(undefined),
  customerId: uuid,
  invoiceId: uuid,
  /** Notes tagged to this project. */
  projectId: uuid,
  from: isoDate,
  to: isoDate,
})
export type CreditNoteSearch = z.infer<typeof creditNoteSearchSchema>

export type CreditNoteListParams = Omit<CreditNoteListQuery, 'page' | 'pageSize' | 'hasOpenCredit'> & {
  page: number
  pageSize: number
  sort: string
  /** Posted notes with credit left to allocate or refund. */
  hasOpenCredit?: boolean | undefined
}

export type CreditNoteSummaryParams = Pick<
  CreditNoteListParams,
  'q' | 'kind' | 'customerId' | 'invoiceId' | 'from' | 'to' | 'createdBy' | 'projectId'
>

export function toCreditNoteListQuery(search: CreditNoteSearch): CreditNoteListParams {
  const view = search.view
  return {
    page: search.page ?? 1,
    pageSize: search.pageSize ?? PAGE_SIZES[0],
    sort: search.sort ?? creditNoteDefaultSort(view),
    q: search.q || undefined,
    kind: search.kind,
    status: view === 'drafts' ? 'DRAFT' : view === 'void' ? 'VOID' : undefined,
    hasOpenCredit: view === 'withCredit' ? true : undefined,
    customerId: search.customerId,
    invoiceId: search.invoiceId,
    projectId: search.projectId,
    from: search.from,
    to: search.to,
  }
}

export function toCreditNoteSummaryQuery(query: CreditNoteListParams): CreditNoteSummaryParams {
  return {
    q: query.q,
    kind: query.kind,
    customerId: query.customerId,
    invoiceId: query.invoiceId,
    projectId: query.projectId,
    from: query.from,
    to: query.to,
    createdBy: query.createdBy,
  }
}

/**
 * New credit note: `?invoiceId=` credits that invoice (a return by default), `?customerId=`
 * preselects the customer (the customer profile's New credit note, SD-62), `?kind=` picks the kind,
 * `?returnTo=` is where saving goes back to (the invoice it was started from).
 */
export const newCreditNoteSearchSchema = z.object({
  invoiceId: uuid,
  customerId: uuid,
  kind: z.enum(CREDIT_NOTE_KINDS).optional().catch(undefined),
  returnTo,
})

// ── receivables reports ──────────────────────────────────────────────────────

export const agingSearchSchema = z.object({ asOf: isoDate })
export const statementSearchSchema = z.object({ from: isoDate, to: isoDate })
